// Read each var as a literal process.env member so webpack inlines it; a bare process.env throws in the browser.
const NEW_RELIC_ACCOUNT_ID = process.env.REACT_APP_NEW_RELIC_ACCOUNT_ID;
const NEW_RELIC_BROWSER_LICENSE_KEY = process.env.REACT_APP_NEW_RELIC_BROWSER_LICENSE_KEY;
const NEW_RELIC_DASHBOARD_APP_ID = process.env.REACT_APP_NEW_RELIC_DASHBOARD_APP_ID;
const NEW_RELIC_LEARNER_APP_ID = process.env.REACT_APP_NEW_RELIC_LEARNER_APP_ID;
const NEW_RELIC_LEARNER_HOSTS = process.env.REACT_APP_NEW_RELIC_LEARNER_HOSTS;

const DEFAULT_LEARNER_HOSTS = [
  'l.easyllama.com',
  'trainees.easyllama.com',
  'l.easyllama.tech',
  'trainees.easyllama.tech',
  'l.easyllamastaging.com',
  'trainees.easyllamastaging.com',
  'l.easyllama.dev',
  'trainees.easyllama.dev',
  'l.easyllama.local',
  'trainees.easyllama.local',
];

function resolve(overrideValue, fallbackValue) {
  return overrideValue !== undefined ? overrideValue : fallbackValue;
}

function getLearnerHosts(env) {
  const raw = resolve(env.REACT_APP_NEW_RELIC_LEARNER_HOSTS, NEW_RELIC_LEARNER_HOSTS);

  if (!raw) {
    return DEFAULT_LEARNER_HOSTS;
  }

  return raw.split(',').map((host) => host.trim().toLowerCase()).filter(Boolean);
}

export function buildNewRelicBrowserOptions(env = {}, hostname = window.location.hostname) {
  const accountID = resolve(env.REACT_APP_NEW_RELIC_ACCOUNT_ID, NEW_RELIC_ACCOUNT_ID);
  const licenseKey = resolve(env.REACT_APP_NEW_RELIC_BROWSER_LICENSE_KEY, NEW_RELIC_BROWSER_LICENSE_KEY);
  const dashboardAppID = resolve(env.REACT_APP_NEW_RELIC_DASHBOARD_APP_ID, NEW_RELIC_DASHBOARD_APP_ID);
  const learnerAppID = resolve(env.REACT_APP_NEW_RELIC_LEARNER_APP_ID, NEW_RELIC_LEARNER_APP_ID);
  const learnerHosts = getLearnerHosts(env);

  const applicationID = learnerHosts.includes(hostname) ? learnerAppID : dashboardAppID;

  if (!accountID || !licenseKey || !applicationID) {
    return null;
  }

  return {
    init: {
      // Off until allowed_origins is set to the API host; its CORS already accepts the tracing headers.
      distributed_tracing: { enabled: false },
      logging: { enabled: false },
      session_replay: { enabled: false },
      session_trace: { enabled: false },
      privacy: { cookies_enabled: true },
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey,
      applicationID,
      sa: 1,
    },
    loader_config: {
      accountID,
      trustKey: accountID,
      agentID: applicationID,
      licenseKey,
      applicationID,
    },
  };
}

// Dynamic so a vendor failure rejects here instead of aborting the calling pack.
async function startAgent(options) {
  const [{ Agent }, { Ajax }, { GenericEvents }, { JSErrors }, { Metrics }, { PageViewEvent }, { PageViewTiming }, { SoftNav }] =
    await Promise.all([
      import('@newrelic/browser-agent/loaders/agent'),
      import('@newrelic/browser-agent/features/ajax'),
      import('@newrelic/browser-agent/features/generic_events'),
      import('@newrelic/browser-agent/features/jserrors'),
      import('@newrelic/browser-agent/features/metrics'),
      import('@newrelic/browser-agent/features/page_view_event'),
      import('@newrelic/browser-agent/features/page_view_timing'),
      import('@newrelic/browser-agent/features/soft_navigations'),
    ]);

  new Agent({ ...options, features: [Ajax, GenericEvents, JSErrors, Metrics, PageViewEvent, PageViewTiming, SoftNav] });
}

export async function initializeNewRelicBrowser() {
  // On window, set before any await, because company and ui-dashboard-bundle both call this on one page.
  if (window.__easyllamaNewRelicBrowserInitialized) {
    return false;
  }
  window.__easyllamaNewRelicBrowserInitialized = true;

  // The Rails-injected agent is already on this page.
  if (window.NREUM && window.NREUM.info) {
    return false;
  }

  const options = buildNewRelicBrowserOptions();
  if (!options) {
    return false;
  }

  try {
    await startAgent(options);
    return true;
  } catch (error) {
    console.warn('New Relic browser agent failed to start', error);
    return false;
  }
}
